import React, {useEffect, useImperativeHandle, useState} from "react";
import ArrowRightIcon from "@material-ui/icons/ArrowRight";
import {createIcon, memo} from "../../common";
import {TextBox} from "../../input/TextBox";
import {Scrollbar} from "../../Scrollbar";
import {makeStyles} from "@material-ui/core";
import { View } from "@codeArt/components/html/View";
import clsx from "clsx";
import Tooltip from "@codeArt/components/Tooltip";

const useStyles = makeStyles(theme => ({
  root: {
    position: "relative",
    flexDirection: "row",
    display: "flex",
    flexWrap: "nowrap",
    [theme.breakpoints.up("sm")]: {
      margin: "-30px"
    }
  },
  header: {
    display: "flex",
    position: "relative",
    flexDirection: "column",
    flexWrap: "nowrap",
    borderBottom: "solid 1px #8A92A5"
  },
  boby: {
    backgroundColor: "#FFF",
    position: "relative",
    zIndex: 9
  },
  side: {
    flex: "0 1 220px !important",
    maxWidth: "220px !important",
    minWidth: "220px !important",
    display: "flex",
    position: "relative",
    zIndex: 2,
    flexDirection: "column",
    flexWrap: "nowrap"
  },
  sideContent: {
    display: "flex",
    position: "relative",
    flexDirection: "column",
    flexWrap: "nowrap"
  },
  moduleNav: {
    display: "flex",
    position: "relative",
    flexDirection: "column",
    flexWrap: "nowrap",
    listStyle: "none",
    paddingLeft: 0,
    justifyContent: "center"
  },
  moduleLabel: {
    padding: "12px 29px"
  },
  itemLi: {
    display: "flex",
    flexDirection: "column",
    flexWrap: "nowrap",
    listStyle: "none"
  },
  itemSpan: () => {
    return {
      color: "#a6a6a6",
      flexWrap: "wrap",
      textDecoration: "none",
      fontSize: "1rem",
      verticalAlign: "middle",
      "&.active": {
        color: "#3f51b5 !important"
      },
      "&:not(.active):hover": {
        color: "#575757 !important"
      }
    };
  }
}));

const _Group = React.forwardRef(({header,footer,items, searchable, onChange}, ref) => {
  const [ index, setIndex ] = useState(-1);
  const classes = useStyles({});

  const getDefault = () => {
    if (!items || items.length===0) return -1;
    return items.find(t => t.default === true);
  };

  const isSelected = (v, i) => {
    if (index === -1) return v.default === true;
    else return index === i;
  };

  const current = () => {
    if (!items || items.length===0) return null;
    return items[index] || getDefault();
  };

  useEffect(() => {
      let c = current();
      if(c){
        if(onChange) onChange(c);
      }
    },
    [ index ]
  );

  useImperativeHandle(ref, () => ({
    current: current
  }));

  const handleClickItem = (item, i) => {
    setIndex(i);
  };

  const renderMenuItem =({spanClassName,icon,pClassName,text,label})=>{
    return (
      <span
      className={clsx(
        classes.itemSpan,
        spanClassName,
        "d-flex flex-row justify-content-start align-items-center c-py-2 c-px-0 c-pointer"
      )}
    >
      {createIcon(icon || ArrowRightIcon, "c-icon-s1d5")}
      <p
        className={`c-flex-1 c-text-s1d5 ${pClassName} c-ellipsis-1 c-mb-0`}
      >
        {text || label}
      </p>
    </span>
    );
  }

  const menuItem = (item, i) => {
    let {icon, text, label} = item;
    let selected = isSelected(item, i);
    let pClassName = icon ? "pl-2" : "";
    let spanClassName = selected ? "active" : "";
    return (
      <li
        className={clsx(classes.itemLi)}
        key={`menu-${i}`}
        onClick={() => {
          handleClickItem(item, i);
        }}
      >
        {
          item.tooltip?
          Tooltip({
            description: <View text={item.tooltip}></View>,
            content:renderMenuItem({spanClassName,icon,pClassName,text,label}),
            placement:"right"
          })
          :renderMenuItem({spanClassName,icon,pClassName,text,label})
        }
      </li>
    );
  };

  const renderSection = (item, i) => {
    return (
      <li
        key={`menu-section-${i}`}
        className={clsx(
          classes.moduleLabel,
          "c-py-2d5 c-px-0 c-text-s1d5 m-text-dark75"
        )}
      >
        {item.section}
      </li>
    );
  };

  const renderItem = (item, i) => {
    let {section} = item;
    if (section) {
      return renderSection(item, i);
    } else {
      return menuItem(item, i);
    }
  };

  const menus = () => {
    if (!items || items.length===0) return null;
    return (
      <ul key={"items"} className={classes.moduleNav}>
        {items.map((item, i) => renderItem(item, i))}
      </ul>
    );
  };

  const renderSearch = () => {
    if (!searchable) return;
    return (
      <div className={clsx(classes.header, "c-l0")}>
        <TextBox
          className="c-mb-0"
          label="搜索"
          width={205}
          maxlength={100}
          size="small"
          variant="outlined"
        />
      </div>
    );
  };

  const sideBar = () => {
    return (
      <div className={clsx(classes.side, "pr-2")}>
        {header}
        {renderSearch()}
        <div className={clsx(classes.sideContent, "c-flex-1")}>
          <Scrollbar>{menus()}</Scrollbar>
        </div>
        {footer}
      </div>
    );
  };

  return (
    <div className={clsx(classes.root, "c-datatable-group c-m-0 h-100 c-lr")}>
      <div className={clsx(classes.boby, "d-flex c-l0")}>{sideBar()}</div>
    </div>
  );
});

export const Group = React.memo(_Group, (prevProps, nextProps) => {
  return memo(prevProps, nextProps, []);
});
